// Derives banner copy / progress state from an EngagementSummary. Shared by
// PathToRewardsStrip (Engagement page), the dashboard preview card, and the
// header streak dropdown so the three surfaces never drift.
// Port of the design canvas's renderVals() banner/path logic
// (scratchpad/engagement_canvas.dc.html), generalised over the milestones[]
// array the API returns instead of hardcoded 7/30-day thresholds.
//
// NOTE (deviation from the Phase 3 contract): the API has no explicit
// "at risk" flag. The design's red/warning banner variant is reproduced here
// via a client-side heuristic (evening hours before the 05:30 IST day
// rollover) rather than invented as a fake field — flag this to product/
// backend if a real signal (e.g. from eng_streak_at_risk) should replace it.

import {EngagementSummary, MilestoneVM} from './types';

export type BannerVariant =
  | 'fresh'
  | 'progress'
  | 'oneAway'
  | 'eligible'
  | 'atRisk';

export interface EngagementPresentation {
  variant: BannerVariant;
  bannerText: string;
  // 0-100, toward the next not-yet-achieved milestone (or the following one
  // once the first is achieved this period).
  progressPct: number;
  progressLeftLabel: string;
  progressRightLabel: string;
  // Milestones sorted ascending by days, for the day-dot path + prize cards.
  milestonesSorted: MilestoneVM[];
  // The smallest milestone's day threshold — drives the day-dot count (7
  // in the locked design; falls back to 7 if no milestones are configured).
  firstMilestoneDays: number;
}

// Day boundary is ~00:00 UTC (05:30 IST, see plan risk #3) — flag "at risk"
// once we're in the evening run-up to it, mirroring the header nudge window.
const AT_RISK_UTC_HOUR = 18;

function isNearDayBoundary(serverDateIso?: string): boolean {
  const now = serverDateIso ? new Date(serverDateIso) : new Date();
  return now.getUTCHours() >= AT_RISK_UTC_HOUR;
}

export function computeEngagementPresentation(
  summary: EngagementSummary,
  opts: {forceAtRisk?: boolean} = {},
): EngagementPresentation {
  const streak = summary.streak.streakCount;
  const todayComplete = summary.streak.todayComplete;
  const milestonesSorted = [...summary.milestones].sort(
    (a, b) => a.days - b.days,
  );
  const first = milestonesSorted[0];
  const firstMilestoneDays = first?.days ?? 7;

  const eligibleFirst = !!first && streak >= first.days;
  const daysToFirst = first ? Math.max(0, first.days - streak) : 0;

  // The progress bar chases the WHOLE ladder, not just the first two
  // milestones: `next` is the first not-yet-achieved milestone ahead of the
  // streak, `last` is the final (monthly) one. Once the streak clears every
  // milestone (`next` undefined, given `milestonesSorted` is non-empty) the
  // bar/eligible copy switch to an "all achieved" state instead of pinning
  // at the old second-milestone's day count forever.
  const last = milestonesSorted[milestonesSorted.length - 1];
  const next = milestonesSorted.find(m => streak < m.days);
  const daysToNext = next ? Math.max(0, next.days - streak) : 0;

  const atRisk =
    opts.forceAtRisk ??
    (!todayComplete && streak > 0 && isNearDayBoundary(summary.serverDate));

  let variant: BannerVariant;
  let bannerText: string;

  if (atRisk) {
    variant = 'atRisk';
    bannerText = `Your ${streak}-day streak is at risk — win 1 game before midnight!`;
  } else if (streak === 0) {
    variant = 'fresh';
    bannerText = 'Start today — win 1 game to light your streak';
  } else if (eligibleFirst) {
    variant = 'eligible';
    bannerText = next
      ? `You're in this week's draw! 🎉 ${daysToNext} more days → ${
          next.label ?? next.prize.title + ' draw'
        }`
      : `You're in the ${last?.prize.title} draw! 🎉`;
  } else if (first && daysToFirst === 1) {
    variant = 'oneAway';
    bannerText = todayComplete
      ? `Tomorrow you become eligible for the ${first.prize.short}!`
      : `Win today → you become eligible for the ${first.prize.short}!`;
  } else if (first) {
    variant = 'progress';
    bannerText = `${daysToFirst} more days in a row → eligible for the ${first.prize.short}`;
  } else {
    variant = 'progress';
    bannerText = 'Keep your streak alive to unlock prize draws';
  }

  const progressDenominator = next?.days ?? last?.days ?? firstMilestoneDays;
  const progressPct = Math.min(
    100,
    Math.round((streak / (progressDenominator || 1)) * 100),
  );
  const progressLeftLabel = `${Math.min(
    streak,
    progressDenominator,
  )} of ${progressDenominator} days`;
  // Prefer each milestone's display `label` (e.g. "Week 2"/"Week 3" for the
  // day-14/day-21 checkpoints) over its prize fields — checkpoints don't
  // carry a real prize, only weekly_draw/monthly milestones do. Falls back to
  // the last (all-achieved) milestone once there's no `next` one left.
  const progressRightLabel =
    next?.label ??
    next?.prize.short ??
    last?.label ??
    last?.prize.short ??
    'Weekly Draw';

  return {
    variant,
    bannerText,
    progressPct,
    progressLeftLabel,
    progressRightLabel,
    milestonesSorted,
    firstMilestoneDays,
  };
}
